const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface FaceEmbeddingItem {
  id: string;
  label: string;
  source_image_url: string | null;
  image_url: string | null;
  created_at: string;
}

export interface FaceRegisterResponse {
  id: string;
  user_id: string;
  label: string;
  source_image_url: string | null;
  created_at: string;
}

function normalizeDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg?: unknown }).msg ?? "");
        }
        return "";
      })
      .filter(Boolean)
      .join("; ");
  }
  return null;
}

async function readErrorMessage(res: Response): Promise<string | null> {
  const rawText = await res.text().catch(() => "");
  if (!rawText) return null;

  try {
    const parsed = JSON.parse(rawText) as { detail?: unknown };
    return normalizeDetail(parsed?.detail) ?? rawText;
  } catch {
    return rawText;
  }
}

// Returns an i18n key for known backend errors so the caller can render it with t().
function translateFaceUploadError(message: string): string {
  const normalized = message.toLowerCase();

  if (
    normalized.includes("largest face too small") ||
    normalized.includes("face bounding box covers")
  ) {
    return "faceEnroll.error.faceTooSmall";
  }

  if (normalized.includes("no face detected")) {
    return "faceEnroll.error.noFaceDetected";
  }

  if (normalized.includes("could not decode image")) {
    return "faceEnroll.error.invalidImage";
  }

  if (normalized.includes("face extraction failed:")) {
    return message.replace(/^Face extraction failed:\s*/i, "");
  }

  return message;
}

function isNetworkFetchError(err: unknown): boolean {
  return (
    err instanceof TypeError ||
    (err instanceof Error &&
      (err.message.includes("Load failed") ||
        err.message.includes("Failed to fetch")))
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("faceEnroll.error.readFileFailed"));
    reader.readAsDataURL(file);
  });
}

export const faceService = {
  list: async (userId: string): Promise<FaceEmbeddingItem[]> => {
    const res = await fetch(`${BASE_URL}/users/${userId}/face`, {
      headers: { ...authHeaders() },
    });
    if (!res.ok) throw new Error("faceEnroll.error.loadFailed");
    return res.json();
  },

  upload: async (
    userId: string,
    file: File,
    label: string
  ): Promise<FaceRegisterResponse> => {
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label);

      const res = await fetch(`${BASE_URL}/users/${userId}/face`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: form,
      });
      if (!res.ok) {
        const message = await readErrorMessage(res);
        throw new Error(
          translateFaceUploadError(message ?? "faceEnroll.error.uploadFailed")
        );
      }
      return res.json();
    } catch (err) {
      if (!isNetworkFetchError(err)) {
        throw err;
      }

      // Fallback for environments where multipart upload intermittently fails.
      const dataUrl = await fileToDataUrl(file);
      const base64 = dataUrl.includes(",") ? dataUrl.split(",", 2)[1] : dataUrl;
      const form = new FormData();
      form.append("image_base64", base64);
      form.append("label", label);

      const res = await fetch(`${BASE_URL}/users/${userId}/face/base64`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: form,
      });
      if (!res.ok) {
        const message = await readErrorMessage(res);
        throw new Error(
          translateFaceUploadError(
            message ?? "faceEnroll.error.uploadFaceFailed"
          )
        );
      }
      return res.json();
    }
  },

  getImageUrl: (userId: string, embeddingId: string): string =>
    `${BASE_URL}/users/${userId}/face/${embeddingId}/image`,

  fetchImageBlob: async (
    userId: string,
    embeddingId: string
  ): Promise<string> => {
    try {
      const res = await fetch(
        `${BASE_URL}/users/${userId}/face/${embeddingId}/image`,
        { headers: authHeaders() }
      );
      if (!res.ok) return "";
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch {
      return "";
    }
  },

  delete: async (userId: string, embeddingId: string): Promise<void> => {
    const res = await fetch(`${BASE_URL}/users/${userId}/face/${embeddingId}`, {
      method: "DELETE",
      headers: { ...authHeaders() },
    });
    if (!res.ok) throw new Error("faceEnroll.error.deleteFailed");
  },
};
